import { config, secureLog, sanitizeInput, isValidEmail, isValidPhone } from '../config/secure-config.js';
import { pushToDataLayer } from './loadGTM.js';

// Rate limiting
const rateLimiter = {
  attempts: new Map(),
  maxAttempts: 5,
  windowMs: 60000, // 1 minute
  
  canAttempt(key) {
    const now = Date.now();
    const userAttempts = this.attempts.get(key) || [];
    const recentAttempts = userAttempts.filter(time => now - time < this.windowMs);
    
    if (recentAttempts.length >= this.maxAttempts) {
      return false;
    }
    
    this.attempts.set(key, [...recentAttempts, now]);
    return true;
  }
};

// Get user's public IP address with timeout
async function getUserIP() {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 3000); // 5 second timeout
    
    const response = await fetch('https://api.ipify.org?format=json', {
      signal: controller.signal
    });
    clearTimeout(timeoutId);
    
    const data = await response.json();
    return data.ip || 'unknown';
  } catch (error) {
    secureLog('Failed to get IP:', error.message);
    return 'unknown';
  }
}

// Generate a secure session ID
function generateSessionId() {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
}

// Validate form data
export function validateFormData(formData) {
  const errors = {};
  
  // Required fields validation
  if (!formData.businessName || formData.businessName.trim().length < 2) {
    errors.businessName = 'שם העסק חייב להכיל לפחות 2 תווים';
  }
  
  if (!formData.name || formData.name.trim().length < 2) {
    errors.name = 'שם מלא חייב להכיל לפחות 2 תווים';
  }
  
  if (!formData.email || !isValidEmail(formData.email)) {
    errors.email = 'כתובת אימייל לא תקינה';
  }
  
  if (!formData.contactPhone || !isValidPhone(formData.contactPhone)) {
    errors.contactPhone = 'מספר טלפון לא תקין';
  }
  
  return {
    valid: Object.keys(errors).length === 0,
    errors: errors
  };
}

// Optimize and validate image
async function optimizeImage(base64String) {
  return new Promise((resolve) => {
    if (!base64String || typeof base64String !== 'string') {
      resolve(null);
      return;
    }

    // Validate base64 format
    const base64Regex = /^data:image\/(jpeg|jpg|png|webp);base64,/;
    if (!base64Regex.test(base64String)) {
      secureLog('Invalid image format');
      resolve(null);
      return;
    }

    try {
      const img = new Image();
      img.onload = function() {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        const maxSize = 1200;
        let width = img.width;
        let height = img.height;
        
        // Resize if needed
        if (width > height && width > maxSize) {
          height = (height * maxSize) / width;
          width = maxSize;
        } else if (height > maxSize) {
          width = (width * maxSize) / height;
          height = maxSize;
        }
        
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);
        
        // Compress to JPEG with 85% quality
        resolve(canvas.toDataURL('image/jpeg', 0.85));
      };
      
      img.onerror = function() {
        secureLog('Failed to load image');
        resolve(null);
      };
      
      img.src = base64String;
    } catch (error) {
      secureLog('Image optimization error:', error.message);
      resolve(null);
    }
  });
}

// Main form submission handler with security
// פונקציית submitForm מתוקנת - החלף את הפונקציה הקיימת בזו:

export async function submitForm(formData) {
  secureLog('Starting secure form submission');
  
  // Rate limiting check
  const userKey = formData.email || 'anonymous';
  if (!rateLimiter.canAttempt(userKey)) {
    return {
      success: false,
      error: 'יותר מדי ניסיונות. אנא נסה שוב בעוד דקה.'
    };
  }
  
  try {
    // Validate form data
    const validation = validateFormData(formData);
    if (!validation.valid) {
      return {
        success: false,
        error: Object.values(validation.errors).join(', ')
      };
    }
    
    // Sanitize key text inputs
    const sanitizedData = {
      ...formData,
      businessName: sanitizeInput(formData.businessName),
      businessType: sanitizeInput(formData.businessType || ''),
      name: sanitizeInput(formData.name),
      email: sanitizeInput(formData.email).toLowerCase(),
      contactPhone: sanitizeInput(formData.contactPhone),
      phone: sanitizeInput(formData.phone || ''),
      address: sanitizeInput(formData.address || ''),
      openingHours: sanitizeInput(formData.openingHours || '')
    };
    
    // Track submission attempt
    pushToDataLayer({
      event: 'form_submission_attempt',
      business_name: sanitizedData.businessName
    });

    // Get reCAPTCHA token if enabled - עם טיפול בשגיאות
    let recaptchaToken = '';
    if (config.recaptcha.enabled && window.grecaptcha) {
      try {
        recaptchaToken = await window.grecaptcha.execute(config.recaptcha.siteKey, { 
          action: 'submit' 
        });
      } catch (error) {
        secureLog('reCAPTCHA error:', error.message);
        // ממשיכים גם בלי reCAPTCHA
      }
    }

    // Get user IP with shorter timeout
    let userIP = 'unknown';
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 4000); // 4 שניות
      
      const response = await fetch('https://api.ipify.org?format=json', {
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      
      if (response.ok) {
        const data = await response.json();
        userIP = data.ip || 'unknown';
      }
    } catch (error) {
      secureLog('Failed to get IP:', error.message);
      // ממשיכים עם IP unknown
    }
    
    // Prepare submission data
    const submissionData = {
      ...sanitizedData,
      submittedAt: new Date().toISOString(),
      recaptchaToken,
      ip: userIP,
      userAgent: navigator.userAgent.substring(0, 200),
      referrer: document.referrer ? new URL(document.referrer).hostname : 'direct',
      sessionId: generateSessionId(),
      version: '1.0.0'
    };

    // Optimize images if present - לא נחכה לזה יותר מדי
    if (submissionData.logo) {
      try {
        submissionData.logo = await optimizeImage(submissionData.logo);
      } catch (e) {
        secureLog('Logo optimization failed:', e);
      }
    }
    
    if (submissionData.images && Array.isArray(submissionData.images)) {
      try {
        submissionData.images = await Promise.all(
          submissionData.images.slice(0, 5).map(img => optimizeImage(img))
        );
      } catch (e) {
        secureLog('Images optimization failed:', e);
      }
    }

    // Submit to Google Apps Script
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 20000); // 10 שניות במקום 30
    
    try {
      // Google Apps Script דורש JSON ועובד רק עם mode: 'no-cors'
      const response = await fetch(config.googleScript.url, {
        method: 'POST',
        mode: 'no-cors', // חשוב! Google Apps Script לא תומך ב-CORS
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          data: submissionData
        }),
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      // במצב no-cors לא נוכל לקרוא את התשובה
      // נניח שהצליח אם לא היתה שגיאה בשליחה
      secureLog('Form submitted successfully (no-cors mode)');
      
      // Track successful submission
      pushToDataLayer({
        event: 'form_submission_success',
        business_name: sanitizedData.businessName
      });
      
      // Clear stored form data
      try {
        localStorage.removeItem('formProgress');
        sessionStorage.removeItem('formSessionId');
      } catch (e) {
        // Ignore storage errors
      }
      
      return {
        success: true,
        message: 'הטופס נשלח בהצלחה! נחזור אליך בהקדם.'
      };
      
    } catch (error) {
      clearTimeout(timeoutId);
      
      if (error.name === 'AbortError') {
        pushToDataLayer({
          event: 'form_submission_timeout',
          business_name: formData.businessName
        });
        
        return {
          success: false,
          error: 'הבקשה לקחה יותר מדי זמן. אנא נסה שוב.'
        };
      }
      
      // Log the actual error for debugging
      secureLog('Submission error:', error.message);
      
      pushToDataLayer({
        event: 'form_submission_error',
        error_message: error.message,
        business_name: formData.businessName
      });
      
      return {
        success: false,
        error: 'אירעה שגיאה בשליחת הטופס. אנא נסה שוב מאוחר יותר.'
      };
    }
    
  } catch (error) {
    secureLog('Submission error:', error.message);
    
    pushToDataLayer({
      event: 'form_submission_error',
      error_message: error.message,
      business_name: formData.businessName
    });
    
    return {
      success: false,
      error: 'אירעה שגיאה בשליחת הטופס. אנא נסה שוב מאוחר יותר.'
    };
  }
}

// Save form progress securely
export async function saveFormProgress(formData) {
  try {
    const sanitizedData = {
      businessName: sanitizeInput(formData.businessName || ''),
      businessType: formData.businessType || '',
      hasPhysicalStore: formData.hasPhysicalStore || '',
      colors: formData.colors || [],
      timestamp: new Date().toISOString()
    };
    
    // Don't save sensitive data like email, phone, etc.
    localStorage.setItem('formProgress', JSON.stringify(sanitizedData));
  } catch (error) {
    secureLog('Failed to save progress:', error.message);
  }
}

export default { submitForm, saveFormProgress, validateFormData };
